import { Button } from '@curri/components'
import { useState } from 'react'

import { useCopyToClipboard } from '../../useCopyToClipboard.ts'

// A credential rendered masked-first, with reveal and copy beside it.
//
// Masked by default because this screen is exactly the one people open on a
// shared screen or a support call: the value should never be on the wire to a
// projector unless someone asked for it. The mask is a fixed run of dots so the
// masked rendering does not even leak the key's length.

/** Exported because the Basic-auth card masks its assembled token the same way,
 *  and two masks of different lengths would imply the values differ in length. */
export const SECRET_MASK = '•'.repeat(28)

/**
 * Copy-to-clipboard with a transient "Copied" acknowledgement. `Copied` only
 * appears when the write succeeded — a blocked clipboard must not claim
 * otherwise, and the reveal control remains the fallback.
 */
export const CopyButton = ({ value }: { value: string }) => {
  const { copied, copy } = useCopyToClipboard()

  return (
    <Button
      label={copied ? 'Copied' : 'Copy'}
      onClick={() => copy(value)}
      size="small"
      type="button"
      variant="tertiary"
    />
  )
}

/**
 * The value, its mask, and the two controls. Rendered inline so the caller's
 * row supplies the label (a pill, a heading) and this supplies the rest.
 */
export const Secret = ({ value }: { value: string }) => {
  const [revealed, setRevealed] = useState(false)

  return (
    <>
      <code
        style={{
          fontFamily: 'var(--curri-font-mono)',
          fontSize: 13,
          color: revealed ? 'var(--curri-text)' : 'var(--curri-text-tertiary)',
          overflowWrap: 'anywhere',
          ...(revealed ? {} : { letterSpacing: '0.08em' }),
        }}
      >
        {revealed ? value : SECRET_MASK}
      </code>
      <span style={{ flex: '1 1 24px' }} />
      <Button
        label={revealed ? 'Hide' : 'Reveal'}
        onClick={() => setRevealed(current => !current)}
        size="small"
        type="button"
        variant="tertiary"
      />
      <CopyButton value={value} />
    </>
  )
}
